import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../../api/authApi';
import { User, LoginRequest } from '../../types';
import {
  clearStoredSession,
  getStoredRefreshToken,
  getStoredToken,
  getStoredUser,
  isSessionIdleExpired,
  markSessionActivity,
  rememberStaleRefreshToken,
  saveStoredTokens,
  saveStoredUser,
} from '../../utils/session';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// A tab that opens after the idle window has passed starts signed out, whatever is still sitting in
// storage - that stored token is exactly what used to let an abandoned browser back in.
const hasLiveSession = !!getStoredToken() && !isSessionIdleExpired();
if (!hasLiveSession) {
  rememberStaleRefreshToken(getStoredRefreshToken());
  clearStoredSession();
}

const storedUser = hasLiveSession ? getStoredUser() : null;

const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  isAuthenticated: hasLiveSession,
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      const user: User = {
        userId: response.userId,
        tenantId: response.tenantId,
        organizationId: response.organizationId,
        email: response.email,
        fullName: response.fullName,
        companyName: response.companyName,
        roleName: response.roleName,
        roles: response.roles,
        permissions: response.permissions,
        enabledFeatures: response.enabledFeatures,
        token: response.token,
        refreshToken: response.refreshToken,
        refreshTokenExpiresAt: response.refreshTokenExpiresAt,
        employeeId: response.employeeId || response.Employee_Id?.toString(),
      };
      saveStoredTokens(response.token, response.refreshToken);
      saveStoredUser(user);
      markSessionActivity();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      clearStoredSession();
    },
    setUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        saveStoredUser(state.user);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.error = null;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, setUser, clearError } = authSlice.actions;
export default authSlice.reducer;
